import { useParams } from 'react-router-dom';
import { useInvoiceQuery } from '../lib/PublicQueries';
import Loading from '../../components/Loading';
import { useSetCurrency } from '../../lib/CurrencyHelpers';
import { useSetLocale } from '~/lib/useSetLocale.js';
import Question from '~/components/forms/Question';
import { formatCurrency, roundCurrency } from '~/lib/CurrencyHelpers';
import { formatDateShort } from '~/lib/DateTime';
import { BusinessProvider } from '~/lib/BusinessContext';
import { InvoiceProvider } from '~/lib/InvoiceContext';
import { useState } from 'react';
import PaymentForm from '~/components/payments/PaymentForm';
import usePublicPostApi from '~/public/lib/usePublicPostApi';
import ThemedModal from '~/components/ThemedModal';
import * as Toast from '~/lib/Toast';
import FormBackgroundContainer from '~/components/forms/FormBackgroundContainer';
import PageContainer from '~/public/components/PageContainer';
import ThemedForm from '~/components/forms/ThemedForm.jsx';
import FormHeaderBlock from '~/components/forms/FormHeaderBlock.jsx';

const InvoiceViewPage = () => {
    const { token } = useParams();
    const { data, isLoading } = useInvoiceQuery(token);
    useSetLocale(data?.context);
    useSetCurrency(data?.context?.currency);

    const invoice = data?.invoice;
    const business = data?.business;
    const styles = data?.styles || {};

    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [paymentSettings, setPaymentSettings] = useState(null);
    const { save: startPayInvoice, isSaving: isLoadingPaymentSettings } =
        usePublicPostApi(`/invoice/pay`);

    if (isLoading) {
        return <Loading />;
    }

    if (!invoice) {
        return (
            <div className="flex items-center justify-center text-red-600 my-8">
                Invoice not found
            </div>
        );
    }

    // Determine invoice status badge
    const getStatusBadge = () => {
        const status = invoice.status;
        if (status === 'paid') {
            return (
                <div className="px-4 py-2 w-full text-center border border-green-200 bg-green-100 text-green-500 rounded-lg">
                    Paid{' '}
                    {invoice.payments && invoice.payments.length > 0 && (
                        <span>
                            {invoice.payments.map((payment, index) => (
                                <span
                                    key={index}
                                >
                                    {formatCurrency(payment.amount)} on {formatDateShort(payment.created_at)}{' '}
                                </span>
                            ))}
                        </span>
                    )}
                </div>
            );
        } else if (status === 'overdue') {
            return (
                <div className="w-full text-center px-4 py-2  border border-red-300 bg-red-100 text-red-500 rounded-lg">
                    Overdue{' '}
                    {invoice.due_date
                        ? `(due ${formatDateShort(invoice.due_date)})`
                        : ''}
                </div>
            );
        } else {
            return null;
            // return (
            //     <div className="inline-block px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg">
            //         Due {invoice.due_date ? formatDateShort(invoice.due_date) : ''}
            //     </div>
            // );
        }
    };

    function onClickPay() {
        if (!showPaymentForm) {
            setShowPaymentForm(true);

            if (!paymentSettings && !isLoadingPaymentSettings) {
                startPayInvoice({
                    invoiceToken: token,
                    url: window?.location?.href || '',
                }).then(settings => {
                    setPaymentSettings(settings);
                }).catch(err => {
                    setShowPaymentForm(false);
                    Toast.error('Failed to load payment settings, please try again');
                });
            }
        } else {
            setShowPaymentForm(false);
        }
    }

    const canPay = business?.canPay;
    const isPaid = invoice.status === 'paid';
    // Only the outstanding balance is charged, so that is what we offer to collect
    const amountPaid = (invoice.payments || [])
        .filter(payment => payment.processor !== 'skip')
        .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
    const amountDue = roundCurrency(invoice.total - amountPaid);
    const showPayButton = canPay && !isPaid && amountDue > 0;

    return (
        <BusinessProvider business={business}>
            <InvoiceProvider
                questions={invoice.questions || []}
                invoice={invoice}
            >
                <ThemedForm styles={styles}>
                <FormBackgroundContainer styles={styles}>
                    <div className="pt-6">
                        <FormHeaderBlock
                            styles={styles}
                            objectId={business?.id}
                            objectType="invoicebusiness"
                            className="pt-6"
                        />
                    </div>

                    <div className="py-8 px-4">
                        <PageContainer styles={styles} hidePoweredBy={false} classNameNoBackground="bg-white p-3">
                {/* Header with business info and status */}
                <div className="mb-8">
                    <div>{getStatusBadge()}</div>
                </div>

                {/* Render all invoice questions */}
                <div className="space-y-6 lg:min-w-[650px]">
                    {(invoice.questions || []).map((question, index) => {
                        // Get the answer for this question from the answers object
                        const answer = invoice.answers?.[question.id] || null;

                        return (
                            <Question
                                key={question.id || index}
                                question={question}
                                id={question.id}
                                value={answer}
                                setValue={() => {}}
                                isReadOnly={true}
                            />
                        );
                    })}
                </div>

                {/* Pay Invoice Button */}
                {showPayButton && (
                    <div className="mt-6 pt-6">
                        <button
                            type="button"
                            onClick={onClickPay}
                            className="form-button w-full sm:w-auto"
                        >
                            Pay {formatCurrency(amountDue)}
                        </button>
                    </div>
                )}
                        </PageContainer>
                    </div>

                    {/* Payment Modal */}
                    <ThemedModal
                        isOpen={showPaymentForm}
                        onClose={() => setShowPaymentForm(false)}
                    >
                        <div>
                            <h2 className="mb-4">Pay Invoice</h2>
                            {isLoadingPaymentSettings && <Loading />}
                            {!isLoadingPaymentSettings && paymentSettings && (
                                <PaymentForm
                                    type="invoice-token"
                                    id={token}
                                    paymentSettings={paymentSettings}
                                    paymentBtnLabel={`Pay ${formatCurrency(amountDue)}`}
                                />
                            )}
                        </div>
                    </ThemedModal>
                </FormBackgroundContainer>
                </ThemedForm>
            </InvoiceProvider>
        </BusinessProvider>
    );
};

export default InvoiceViewPage;

